import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sendSingleRequest } from '../../apis';
import {
  Button,
  Datepicker,
  Dropdown,
  Spinner,
  Title,
} from '../../components/shared';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Table } from 'antd';
import useMenuHandler from '../../lib/useMenuHandler';
import { formatNumber, numberToMoneyStr } from '../../entities/util-functions';
import CaretDownIcon from '../../components/shared/icons/CaretDownIcon';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { selectFarmsData } from '../../store/farms/farms.selector';
import {
  IHarvestResource,
  IMusselHarvest,
  IOysterHarvest,
} from '../../entities/growing.entities';
import HarvestPDFCodeButton from './HarvestPDFCodeButton';
import { IFarmResource } from '../../entities/farms.entities';
import { selectAccount } from '../../store/auth/auth.selector';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { defaultDateFormat, toSecond } from '../../lib/common.helpers';
import { TBusinessType } from '../../entities/general.entities';
import { lineLengthLabel, weightAmountLabel } from '../../lib/farm.helpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const options = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    title: {
      display: true,
      text: 'Chart.js Bar Chart',
    },
  },
};

interface IChild {
  id: number;
  farm: string;
  line: string;
  quantity: string;
  amount: string;
  length: string;
}

interface IRow {
  id: number;
  cropType: TBusinessType;
  season: string;
  date: number;
  farm: string;
  line: string;
  amount: string;
  length: string;
  income: string;
  is_full: boolean;
  quantity: string;
  lines: IChild[];
}

const convertRow = (
  row: IHarvestResource,
  farms: IFarmResource[],
  unitType: any,
  lang: any,
): IRow => {
  const farmSrc = farms.find(x => x.id === row.line.farm_id)!;
  const lineSrc = farmSrc.lines.find(x => x.id === row.line_id)!;
  let amount = row.is_waste
    ? 'Stripped to waste'
    : row.crop_type === 'MUSSEL'
      ? weightAmountLabel(
          row.crop_type,
          unitType,
          (row as IMusselHarvest).amount,
        )
      : `${formatNumber((row as IOysterHarvest).amount_dz)} dozens`;
  let length = row.is_waste
    ? ''
    : lineLengthLabel(
        row.crop_type,
        lineSrc.line_type,
        lineSrc.farming_method,
        unitType,
        row.line_length,
      );
  let quantity = row.is_waste
    ? ''
    : `${row.bags_quantity ?? 0} ${row.crop_type === 'MUSSEL' ? 'bags' : 'trays'}`;
  const income = row.is_waste ? '' : `$${numberToMoneyStr(row.income ?? 0)}`;

  const uniqueFarmsCount = row.lines
    ? Array.from(
        new Set(
          row.lines.map(
            ln => farms.find(f => f.lines.some(l => l.id === ln.line_id))?.id,
          ),
        ),
      ).filter(k => !!k).length
    : 1;
  const uniqueLinesCount = row.lines?.length ?? 1;

  let children: IChild[] = [];
  if (row.lines && row.lines?.length > 0) {
    let amtSum = 0,
      lenSum = 0,
      qtySum = 0;
    for (const cline of row.lines) {
      const cf = farms.find(f => f.lines.some(l => l.id === cline.line_id))!;
      const cl = cf.lines.find(l => l.id === cline.line_id)!;
      amtSum += cline.amount;
      lenSum += cline.line_length ?? 0;
      qtySum += cline.bags_quantity ?? 0;
      children.push({
        id: cline.id,
        farm: `${cf.name} (${cf.farm_number})`,
        line: cl.line_name,
        quantity: `${cline.bags_quantity ?? 0} ${row.crop_type === 'MUSSEL' ? 'bags' : 'trays'}`,
        amount: row.is_waste
          ? 'Stripped to waste'
          : weightAmountLabel(row.crop_type, unitType, cline.amount),
        length: row.is_waste
          ? ''
          : lineLengthLabel(
              row.crop_type,
              cl.line_type,
              cl.farming_method,
              unitType,
              cline.line_length,
            ),
      });
    }
    amount = weightAmountLabel(row.crop_type, unitType, amtSum);
    length = lineLengthLabel(
      row.crop_type,
      lineSrc.line_type,
      lineSrc.farming_method,
      unitType,
      lenSum,
    );
    quantity = `${qtySum} ${row.crop_type === 'MUSSEL' ? 'bags' : 'trays'}`;
  }

  return {
    id: row.id,
    cropType: row.crop_type,
    season: row.season_name,
    date: row.complete_date,
    farm:
      row.lines && uniqueFarmsCount > 1
        ? translate(lang, '%s farms combined', uniqueFarmsCount)
        : `${farmSrc.name} (${farmSrc.farm_number})`,
    line:
      uniqueLinesCount > 1
        ? translate(lang, '%s lines combined', uniqueLinesCount)
        : row.line.line_name,
    amount,
    length,
    income,
    is_full: row.is_full,
    quantity,
    lines: children,
  };
};

const columns: any = [
  {
    key: 'id',
    title: 'ID',
    dataIndex: 'id',
    render: (v: number) => <span>{v}</span>,
  },
  {
    key: 'season',
    title: 'Season',
    dataIndex: 'season',
    render: (x: string) => <span>{x}</span>,
  },
  {
    key: 'date',
    title: 'Date',
    dataIndex: 'date',
    render: (x: number) => <span>{defaultDateFormat(x)}</span>,
  },
  {
    key: 'farm',
    title: 'Farm',
    dataIndex: 'farm',
    render: (x: string) => <span>{x}</span>,
  },
  {
    key: 'line',
    title: 'Line',
    dataIndex: 'line',
    render: (x: string) => <span>{x}</span>,
  },
  {
    key: 'tones',
    title: 'Amount',
    dataIndex: 'amount',
    render: (x: string) => <span>{x}</span>,
  },
  {
    key: 'income',
    title: 'Income',
    dataIndex: 'income',
    render: (x: string) => <span>{x}</span>,
  },
  {
    key: 'line_length',
    title: '_harvested_length_containers',
    dataIndex: 'length',
    render: (x: string) => <span>{x}</span>,
  },
  {
    key: 'is_full',
    title: 'F/P',
    dataIndex: 'is_full',
    render: (x: boolean) => <span>{!x ? 'Partial' : 'Full'}</span>,
  },
  {
    key: 'quantity',
    title: 'Volume Quantity / Number of Trays',
    dataIndex: 'quantity',
    render: (x: string) => <span>{x}</span>,
    align: 'left',
  },
];

const colors = [
  '#0d88e6',
  '#fd7f6f',
  '#7eb0d5',
  '#b2e061',
  '#bd7ebe',
  '#ffb55a',
  '#ffee65',
  '#beb9db',
  '#fdcce5',
  '#8bd3c7',
  '#4421af',
  '#1a53ff',
];

interface IResponse {
  data: IHarvestResource[];
  offset: number;
  limit: number;
  count: number;
}

interface IFltOption {
  season?: string;
  date_from: number;
  date_to: number;
  farm_id?: number;
  line_id?: number;
}

const getHarvests = async (
  filter: IFltOption | undefined = undefined,
  pageOptions: any = undefined,
): Promise<IResponse | undefined> => {
  let params = {
    season_name: filter?.season ?? undefined,
    date_from: filter?.date_from ? toSecond(filter.date_from) : undefined,
    date_to: filter?.date_to ? toSecond(filter.date_to) : undefined,
    farm_id: filter?.farm_id ?? undefined,
    line_id: filter?.line_id ?? undefined,
    offset: pageOptions
      ? (pageOptions.current - 1) * pageOptions.pageSize
      : undefined,
    limit: pageOptions ? pageOptions.pageSize : 10,
  };
  const res = await sendSingleRequest(
    params,
    'GET',
    'api/overview/harvests',
    true,
  );
  return res.status ? res.data : undefined;
};

const SummaryHarvestPage = () => {
  const account = useSelector(selectAccount);
  const farmsData = useSelector(selectFarmsData);
  const lang = useSelector(selectLang);

  const [harvests, setHarvests] = useState<IRow[]>([]);
  const [lastHarvest, setLastHarvest] = useState<IHarvestResource>();
  const [bestPerformingData, setBestPerformingData] = useState<any>();
  const [catchSpatMonthData, setCatchSpatMonthData] = useState<any>();
  const [lineData, setLineData] = useState<any>();
  const [monthData, setMonthData] = useState<any>();
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { redirectToHarvestDetail } = useMenuHandler();
  const [fltOpt, setFltOpt] = useState<IFltOption>({
    date_from: moment().toDate().getTime(),
    date_to: moment().toDate().getTime(),
  });
  const [seasons, setSeasons] = useState([
    { id: 'All', label: 'All', value: 'All' },
  ]);
  const [loading, setLoading] = useState(31);

  const loadData = useCallback(() => {
    setLoading(0);
    getHarvests().then(res => {
      setLoading(p => p | 1);
      if (res) {
        const tmpHv: IHarvestResource[] = res.data;
        setLastHarvest(tmpHv.find(x => !!x.harvest_number));
        setHarvests(
          tmpHv.map(x => convertRow(x, farmsData, account?.display_unit, lang)),
        );
        setPageOptions({ ...pageOptions, total: res.count });

        const dateMin = tmpHv.reduce(
          (p, c) => (p > c.complete_date ? c.complete_date : p),
          Date.now(),
        );
        const dateMax = tmpHv.reduce(
          (p, c) => (p < c.complete_date ? c.complete_date : p),
          0,
        );
        setFltOpt(prv => ({
          ...prv,
          date_from: 1000 * dateMin,
          date_to: 1000 * dateMax,
        }));
        const uniqueSeasons = Array.from(
          new Set(tmpHv.map(x => x.season_name)),
        );
        let ss = [
          { id: 'All', label: 'All', value: 'All' },
          ...uniqueSeasons.map(x => ({
            id: x,
            label: x,
            value: x,
          })),
        ];
        setSeasons(ss);
      }
    });
    sendSingleRequest(
      { type: 'normal' },
      'GET',
      'api/overview/harvest-summary',
      true,
    ).then(res => {
      setLoading(p => p | 2);
      if (res.status) setMonthData(res.data);
    });
    sendSingleRequest(
      { type: 'catch_spat' },
      'GET',
      'api/overview/harvest-summary',
      true,
    ).then(res => {
      setLoading(p => p | 4);
      if (res.status) setCatchSpatMonthData(res.data);
    });
    sendSingleRequest(
      { type: 'line' },
      'GET',
      'api/overview/harvest-summary',
      true,
    ).then(res => {
      setLoading(p => p | 8);
      if (res.status) setLineData(res.data);
    });
    sendSingleRequest({}, 'GET', 'api/overview/best-perform-line', true).then(
      res => {
        setLoading(p => p | 16);
        if (res.status) setBestPerformingData(res.data);
      },
    );
  }, []);

  const handlePageChange = async (pageOptions: any) => {
    const res = await getHarvests(fltOpt, pageOptions);
    if (!res) return;
    const tmpHv: IHarvestResource[] = res.data;
    setHarvests(
      tmpHv.map(x => convertRow(x, farmsData, account?.display_unit, lang)),
    );
    setPageOptions({ ...pageOptions, total: res.count });
  };
  const changeFilter = async (filter: IFltOption) => {
    const res = await getHarvests(filter, pageOptions);
    if (!res) return;
    const tmpHv: IHarvestResource[] = res.data;
    setHarvests(
      tmpHv.map(x => convertRow(x, farmsData, account?.display_unit, lang)),
    );
    setPageOptions({ ...pageOptions, total: res.count });
    setFltOpt(filter);
  };
  const getHarvestPerYearDataSets = (data: any) => {
    const labels: any = [];
    const datasets: any = [];
    let i = 0;
    for (let y in data) {
      const d: any = {
        label: y,
        data: [],
        backgroundColor: colors[i],
      };
      i = (i + 1) % 12;
      for (let m in data[y]) {
        if (!labels.includes(m)) labels.push(m);
        d.data.push(data[y][m]);
      }
      datasets.push(d);
    }
    return {
      labels,
      datasets,
    };
  };
  const getBestPerformingDataSets = (data: any) => {
    const labels: any = [];
    const datasets: any = [];
    let i = 0;
    for (let f in data) {
      const d: any = {
        label: f,
        data: [],
        backgroundColor: colors[i],
      };
      i = (i + 1) % 12;
      for (let m in data[f]) {
        if (!labels.includes(m)) labels.push(m);
        d.data.push(data[f][m]);
      }
      datasets.push(d);
    }
    return {
      labels,
      datasets,
    };
  };

  const farmsOption = [
    { id: 'All', value: 'ALL', label: 'All' },
    ...farmsData.map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: `${x.name} (${x.farm_number})`,
    })),
  ];
  const linesOption = fltOpt.farm_id
    ? [
        { id: 'All', value: '', label: 'All' },
        ...(farmsData
          .find(x => x.id === fltOpt.farm_id)
          ?.lines.map(x => ({
            id: x.id.toString(),
            value: x.id.toString(),
            label: x.line_name,
          })) ?? []),
      ]
    : null;

  const filterFarmChange = (v: string) => {
    const f = v === 'ALL' ? undefined : farmsData.find(x => x.id === Number(v));
    changeFilter({ ...fltOpt, farm_id: f?.id, line_id: undefined });
  };
  const filterLineChange = (v: string) => {
    if (v === 'ALL') {
      changeFilter({ ...fltOpt, line_id: undefined });
    } else {
      const f = farmsData.find(x => x.lines.some(y => y.id === Number(v)));
      const l = f?.lines.find(x => x.id === Number(v));
      changeFilter({ ...fltOpt, line_id: l?.id });
    }
  };

  const lineColumns = [
    {
      key: 'id',
      title: 'ID',
      render: (chd: IChild) => <span>{chd.id}</span>,
    },
    {
      key: 'farm',
      title: 'Farm',
      render: (chd: IChild) => <span>{chd.farm}</span>,
    },
    {
      key: 'line',
      title: 'Line',
      render: (chd: IChild) => <span>{chd.line}</span>,
    },
    {
      key: 'bags',
      title: 'Volume Quantity',
      render: (chd: IChild) => <span>{chd.quantity}</span>,
    },
    {
      key: 'amount',
      title: 'Amount',
      render: (chd: IChild) => <span>{chd.amount}</span>,
    },
    {
      key: 'length',
      title: '_harvested_length_containers',
      render: (chd: IChild) => <span>{chd.length}</span>,
      align: 'left',
    },
  ];

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className='bg-secondary min-h-100'>
      <div className='container'>
        <div className='overview d-flex justify-between align-center'>
          <Title size={5} color='black' align='default' fontWeight={700}>
            {translate(lang, 'Harvest Stats')}
          </Title>
          <div className='d-flex'>
            <HarvestPDFCodeButton lastHarvest={lastHarvest} />
            <Link to='/summary/processing' className='ml-12'>
              <Button color='blue' size={3} width='middle' type='fill'>
                {translate(lang, '_view_processing_stats')}
              </Button>
            </Link>
          </div>
        </div>
        <div className='chart--content'>
          <div className='row'>
            <div className='chart--card'>
              {!(loading & 2) ? (
                <div className='mt-32 mb-32'>
                  <Spinner />
                </div>
              ) : (
                <div className='chart-card'>
                  <div className='chart--title'>
                    {translate(lang, 'Harvest per year')}
                  </div>
                  <Bar
                    options={options}
                    data={getHarvestPerYearDataSets(monthData)}
                  />
                </div>
              )}
            </div>
            <div className='chart--card'>
              {!(loading & 4) ? (
                <div className='mt-32 mb-32'>
                  <Spinner />
                </div>
              ) : (
                <div className='chart-card'>
                  <div className='chart--title'>
                    {translate(lang, '_harvests_spat_catch_per')}
                  </div>
                  <Bar
                    options={options}
                    data={getHarvestPerYearDataSets(catchSpatMonthData)}
                  />
                </div>
              )}
            </div>
          </div>
          <div className='row'>
            <div className='chart--card'>
              {!(loading & 8) ? (
                <div className='mt-32 mb-32'>
                  <Spinner />
                </div>
              ) : (
                <div className='chart-card'>
                  <div className='chart--title'>
                    {translate(lang, 'Best harvested lines')}
                  </div>
                  <Bar
                    options={options}
                    data={getBestPerformingDataSets(lineData)}
                  />
                </div>
              )}
            </div>
            <div className='chart--card'>
              {!(loading & 16) ? (
                <div className='mt-32 mb-32'>
                  <Spinner />
                </div>
              ) : (
                <div className='chart-card'>
                  <div className='chart--title'>
                    {translate(lang, '_best_performing_lines_harvest')}
                  </div>
                  <Bar
                    options={options}
                    data={getBestPerformingDataSets(bestPerformingData)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='farms__line-content'>
          <div className='harvest-filter-bar'>
            <div>
              <Dropdown
                label={translate(lang, 'Filter by farm')}
                placeholder={translate(lang, 'Select farm')}
                options={farmsOption}
                value={
                  farmsOption.find(x => Number(x.id) === fltOpt.farm_id)?.id ??
                  'ALL'
                }
                onChange={filterFarmChange}
              />
            </div>
            <div>
              <Dropdown
                label={translate(lang, 'Filter by line')}
                placeholder={translate(lang, 'Select line')}
                options={linesOption ?? []}
                value={
                  !linesOption
                    ? undefined
                    : (linesOption?.find(x => Number(x.id) === fltOpt.line_id)
                        ?.id ?? 'ALL')
                }
                onChange={filterLineChange}
                disabled={!linesOption}
              />
            </div>
            <div>
              <Datepicker
                label={translate(lang, 'Date from')}
                defaultValue={fltOpt.date_from}
                onChange={e =>
                  e &&
                  changeFilter({
                    ...fltOpt,
                    date_from: e.toDate().getTime(),
                  })
                }
              />
            </div>
            <div>
              <Datepicker
                label={translate(lang, 'Date to')}
                defaultValue={fltOpt.date_to}
                onChange={e =>
                  e &&
                  changeFilter({ ...fltOpt, date_to: e.toDate().getTime() })
                }
              />
            </div>
            <div>
              <Dropdown
                label={translate(lang, 'Filter by season')}
                value={fltOpt.season ?? 'All'}
                options={seasons}
                onChange={v =>
                  changeFilter({
                    ...fltOpt,
                    season: v.length <= 0 ? undefined : v,
                  })
                }
              />
            </div>
          </div>
          <div className='d-flex farms__main'>
            <div className='w-100 pos-relative overflow-auto'>
              <Table
                rowKey='id'
                className='table table--isFarm'
                pagination={pageOptions}
                onChange={handlePageChange}
                columns={
                  !account?.show_budget_menu
                    ? columns
                        .filter((x: any) => x.key !== 'income')
                        .map((x: any) => ({
                          ...x,
                          title: translate(lang, x.title),
                        }))
                    : columns.map((x: any) => ({
                        ...x,
                        title: translate(lang, x.title),
                      }))
                }
                dataSource={harvests}
                expandable={{
                  expandedRowRender: (row: IRow) => {
                    if (!row.lines || row.lines.length <= 1) {
                      return null;
                    }
                    return (
                      <Table
                        rowKey='id'
                        className='table table--isFarm is__child'
                        columns={lineColumns.map((x: any) => ({
                          ...x,
                          title: translate(lang, x.title),
                        }))}
                        pagination={false}
                        dataSource={row.lines}
                        onRow={x => ({
                          onClick: () => redirectToHarvestDetail(x.id),
                        })}
                      />
                    );
                  },
                  expandIcon: ({ onExpand, record }) => {
                    if (!record.lines || record.lines.length <= 1) {
                      return (
                        <span style={{ display: 'inline-block', width: 20 }} />
                      );
                    }
                    return (
                      <div
                        className='pt-20 pb-20'
                        onKeyDown={() => undefined}
                        onClick={event => {
                          event.stopPropagation();
                          onExpand(record, event);
                        }}
                      >
                        <CaretDownIcon />
                      </div>
                    );
                  },
                  rowExpandable: (record: IRow) =>
                    !!record.lines && record.lines.length > 1,
                }}
                onRow={h => ({
                  onClick: () => redirectToHarvestDetail(h.id),
                })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryHarvestPage;
